import { useState } from 'react';
import { Lock, ArrowRight, FileText, CheckCircle2 } from 'lucide-react';
import PageShell from '@/components/PageShell';

/* ================================================================
   /charter  —  The Mobility Group Charter
   Non-binding founding document · v0.1 · internal + members.
   Passcode-gated (TMG2026).
================================================================ */

const PASSCODE = 'TMG2026';

// ------------------------------------------------------------------
// Data
// ------------------------------------------------------------------
const WHY = [
  {
    n: '01',
    title: 'Growth',
    body: "One member's customer relationships can become opportunities for others. Instead of each company trying to open the same doors alone, we approach them together.",
  },
  {
    n: '02',
    title: 'Joint work',
    body: 'OEM presentations, dealer meetings, industry events, marketing campaigns and thought-leadership work become practical when several members contribute. What is difficult for one becomes achievable for a few.',
  },
  {
    n: '03',
    title: 'Learning',
    body: 'Founders share warm introductions, templates and real-world experiences directly. No consulting layer, no formal process, no overhead — practical knowledge passed between people building similar businesses.',
  },
];

const CONFLICT_RULES = [
  'Before any member pursues a new opportunity — a client, an OEM or a dealer — they flag it briefly in the shared communication channel. This is for visibility.',
  'For new relationships where no member has a prior connection, the member who flags it first has the right to lead that pursuit.',
  'If two or more members already have a relationship with the same prospect, they either co-pitch together or agree on a commercial split before approaching. No one goes alone when someone else in the group already has a foothold.',
  'No member may poach or solicit an existing client of another member. That is grounds for immediate removal.',
];

const VAULT_ITEMS = [
  'Anonymised pitch decks and proposals that have worked.',
  'Procurement contacts and notes from past OEM and dealer engagements.',
  'Aggregated pricing benchmarks — shared in a way that protects individual margin data.',
  'One-page templates for joint proposals and capability statements.',
];

const RHYTHM = [
  {
    cadence: 'Weekly',
    duration: '30 min',
    title: "Founders' huddle",
    body: 'Same time each week. Attendance is optional but preferred — join if you have something active. We cover new-member discussions, live referrals and next actions on ongoing work.',
  },
  {
    cadence: 'Ongoing',
    duration: 'Slack / WhatsApp',
    title: 'Main communication',
    body: 'Day-to-day exchanges — introductions, leads, requests for help and wins. The weekly call exists mainly to unblock what cannot be resolved in chat.',
  },
  {
    cadence: 'Monthly',
    duration: '45 min',
    title: 'Progress review',
    body: 'New members, referrals made, deals closed, one key decision if needed. A one-page summary is circulated afterwards.',
  },
  {
    cadence: 'Quarterly',
    duration: '90 min · virtual or in-person',
    title: 'Meetup',
    body: 'Relationships, lessons learned, and new opportunities. Every quarter should leave the network stronger.',
  },
];

const METRICS = [
  'Number of members added',
  'Number of referrals generated',
  'Number of deals closed',
];

// ------------------------------------------------------------------
// UI helpers
// ------------------------------------------------------------------
const SectionKicker = ({ children }) => (
  <div className="overline text-amber mb-3">
    <span className="inline-flex items-center gap-2">
      <span className="h-px w-6 bg-amber inline-block" />
      {children}
    </span>
  </div>
);

// ------------------------------------------------------------------
// Gate
// ------------------------------------------------------------------
const Gate = ({ onUnlock }) => {
  const [entered, setEntered] = useState('');
  const [error, setError] = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (entered.trim() === PASSCODE) onUnlock();
    else setError('Incorrect passcode.');
  };

  return (
    <PageShell>
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 min-h-[70vh] flex items-center">
        <div className="max-w-lg">
          <div className="overline text-amber mb-3">
            <span className="inline-flex items-center gap-2">
              <Lock className="w-3.5 h-3.5" />
              Internal · members only
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-balance leading-[1.02]">
            TMG Charter.
            <br />
            <span className="text-amber">Members only.</span>
          </h1>
          <p className="mt-6 text-zinc-300 leading-relaxed">
            The founding document of The Mobility Group — non-binding, subject
            to amendment by the founding partners. Enter the shared passcode
            to read.
          </p>
          <form onSubmit={submit} className="mt-8 flex gap-3">
            <input
              type="password"
              value={entered}
              onChange={(e) => {
                setEntered(e.target.value);
                setError('');
              }}
              placeholder="Passcode"
              data-testid="charter-passcode-input"
              className="flex-1 bg-black border border-white/15 px-4 py-3 text-sm font-mono text-white placeholder-zinc-600 focus:outline-none focus:border-amber"
            />
            <button
              type="submit"
              data-testid="charter-passcode-submit"
              className="inline-flex items-center gap-2 border border-amber bg-amber px-5 py-3 text-xs font-mono uppercase tracking-[0.18em] text-black hover:bg-white hover:border-white transition-colors"
            >
              Unlock
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </form>
          {error && (
            <div
              data-testid="charter-passcode-error"
              className="mt-3 text-xs font-mono uppercase tracking-[0.14em] text-rose-400"
            >
              {error}
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
};

// ------------------------------------------------------------------
// Diagrams
// ------------------------------------------------------------------
const HowItWorksDiagram = () => (
  <svg
    viewBox="0 0 1200 380"
    width="100%"
    className="block max-w-5xl mx-auto"
    data-testid="how-it-works-diagram"
  >
    {/* Layer 3 (top) — Shared initiatives */}
    <g>
      <rect x="220" y="40" width="760" height="80" fill="rgba(6,182,212,0.08)" stroke="#06B6D4" strokeWidth="1.5" />
      <text x="600" y="72" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="12" fill="#06B6D4" fontWeight="700" letterSpacing="2">LAYER 3 · SHARED INITIATIVES</text>
      <text x="600" y="97" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="13" fill="#E4E4E7">Joint pitches · exhibition booths · marketing campaigns · shared hiring · common assets</text>
    </g>

    {/* Layer 2 (middle) — Commercial collaboration */}
    <g>
      <rect x="140" y="150" width="920" height="80" fill="rgba(245,158,11,0.08)" stroke="#F59E0B" strokeWidth="1.5" />
      <text x="600" y="182" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="12" fill="#F59E0B" fontWeight="700" letterSpacing="2">LAYER 2 · COMMERCIAL COLLABORATION</text>
      <text x="600" y="207" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="13" fill="#E4E4E7">Referrals · revenue shares · finder&#39;s fees · bilateral agreements between members</text>
    </g>

    {/* Layer 1 (base) — Membership */}
    <g>
      <rect x="60" y="260" width="1080" height="80" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" />
      <text x="600" y="292" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="12" fill="#FFFFFF" fontWeight="700" letterSpacing="2">LAYER 1 · MEMBERSHIP</text>
      <text x="600" y="317" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="13" fill="#A1A1AA">One-page MOU · non-binding · no fees · admission by recommendation and review</text>
    </g>

    {/* Optional labels on the right */}
    <text x="1150" y="80" textAnchor="end" fontFamily="'JetBrains Mono', monospace" fontSize="9" fill="#06B6D4" letterSpacing="1.4">OPTIONAL</text>
    <text x="1150" y="190" textAnchor="end" fontFamily="'JetBrains Mono', monospace" fontSize="9" fill="#F59E0B" letterSpacing="1.4">OPTIONAL</text>
    <text x="1150" y="300" textAnchor="end" fontFamily="'JetBrains Mono', monospace" fontSize="9" fill="#FFFFFF" letterSpacing="1.4">ONLY FORMAL STEP</text>
  </svg>
);

// ------------------------------------------------------------------
// Page
// ------------------------------------------------------------------
const DraftStructure = () => {
  const [unlocked, setUnlocked] = useState(false);
  if (!unlocked) return <Gate onUnlock={() => setUnlocked(true)} />;

  return (
    <PageShell>
      {/* Cover */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="absolute inset-0 radial-glow-amber opacity-25" />
        <div className="relative px-6 md:px-12 lg:px-16 pt-20 pb-16 md:pt-28 md:pb-24">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-4">
            <FileText className="w-3.5 h-3.5" />
            TMG Charter · v0.1 · non-binding
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-balance max-w-5xl leading-[0.98]">
            The Mobility Group.
            <br />
            <span className="text-amber">A network, not a holding company.</span>
          </h1>
          <p className="mt-8 max-w-3xl text-zinc-300 text-base md:text-lg leading-relaxed">
            TMG is a network of independent companies working in mobility and
            automotive technology. Every member remains independently owned
            and operated. TMG is not a holding company and it is not an
            investment vehicle — it is a shared identity, a referral network,
            and a platform for collaboration when it makes commercial sense.
          </p>
          <p className="mt-4 max-w-3xl text-zinc-400 text-sm md:text-base leading-relaxed">
            Membership is straightforward: a one-page memorandum of
            understanding. Non-binding, no fees, no long-term obligations. New
            members are admitted based on a recommendation and a review by
            existing members, so the group stays focused on solid founders and
            businesses.
          </p>
        </div>
      </section>

      {/* Section 1 · Why we started this */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-b border-white/10">
        <SectionKicker>Section 1 · Why we started this</SectionKicker>
        <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-3xl">
          The mobility space is fragmented. Customers want complete solutions.
        </h2>
        <p className="mt-4 text-zinc-400 text-sm md:text-base max-w-3xl leading-relaxed">
          Most companies address only one piece of the puzzle. TMG exists to
          bridge that gap — through three concrete outcomes.
        </p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {WHY.map((d) => (
            <div key={d.n} data-testid={`why-${d.n}`} className="border border-white/10 bg-ink-800 p-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-3">/ {d.n}</div>
              <h3 className="font-display text-2xl text-white leading-tight">{d.title}</h3>
              <p className="mt-4 text-sm text-zinc-300 leading-relaxed">{d.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Section 2 · How it works — with diagram */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-b border-white/10 bg-ink-900/30">
        <SectionKicker>Section 2 · How it works in practice</SectionKicker>
        <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-3xl">
          Three optional layers.
        </h2>
        <p className="mt-4 text-zinc-400 text-sm md:text-base max-w-3xl leading-relaxed">
          Members opt into whichever layers make sense. Only Layer 1 is
          formal — everything above it is voluntary and driven by real
          commercial fit.
        </p>

        <div className="mt-10 border border-white/10 bg-ink-900 p-6 md:p-10">
          <HowItWorksDiagram />
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border border-white/10 bg-ink-800 p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white mb-2">Layer 1 · Membership</div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              The only formal step. One-page MOU, non-binding, exit at any time.
            </p>
          </div>
          <div className="border border-white/10 bg-ink-800 p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-2">Layer 2 · Commercial collaboration</div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              Two members work out terms directly. TMG is not a party to those
              agreements and does not invoice on anyone&apos;s behalf.
            </p>
            <p className="mt-3 text-xs text-zinc-400 leading-relaxed">
              <span className="text-amber">Default guideline:</span> unless
              otherwise agreed, a qualified introduction that leads to a
              closed deal carries a{' '}
              <span className="text-amber">5–10% finder&apos;s fee</span>,
              payable by the receiving member. Members are free to negotiate
              any other arrangement.
            </p>
          </div>
          <div className="border border-white/10 bg-ink-800 p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400 mb-2">Layer 3 · Shared initiatives</div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              A subset of members pool resources for a specific purpose — a
              joint pitch, an exhibition booth, a marketing campaign, shared
              hiring, or a common asset. Only participants contribute and
              decide how to share costs and benefits.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3 · Avoiding conflicts */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-b border-white/10">
        <SectionKicker>Section 3 · Avoiding conflicts</SectionKicker>
        <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-3xl">
          A few clear rules to preserve trust.
        </h2>
        <p className="mt-4 text-zinc-400 text-sm md:text-base max-w-3xl leading-relaxed">
          Conflicts between members are the fastest way to break the network.
          These four rules are about preventing blind spots and preserving the
          trust that makes the group useful.
        </p>
        <div className="mt-10 border border-white/10 bg-ink-800 p-6 md:p-8">
          <ol className="space-y-4 text-sm md:text-base text-zinc-200 leading-relaxed">
            {CONFLICT_RULES.map((r, i) => (
              <li key={i} data-testid={`rule-${i + 1}`} className="flex gap-4">
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber flex-shrink-0 pt-1">
                  R{String(i + 1).padStart(2, '0')}
                </span>
                <span>{r}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Section 4 · Shared knowledge · The Vault */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-b border-white/10 bg-ink-900/30">
        <SectionKicker>Section 4 · Shared knowledge</SectionKicker>
        <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-3xl">
          The Vault.
        </h2>
        <p className="mt-4 text-zinc-400 text-sm md:text-base max-w-3xl leading-relaxed">
          A private shared workspace on Google Drive or Notion. No one is
          required to share proprietary IP — but the more members contribute,
          the more everyone gets out of it. Access is granted to every member
          on day one.
        </p>
        <div className="mt-10 border border-amber/40 bg-amber/5 p-6 md:p-8">
          <ul className="space-y-3 text-sm md:text-base text-zinc-200 leading-relaxed">
            {VAULT_ITEMS.map((v, i) => (
              <li key={i} className="flex gap-3">
                <CheckCircle2 className="w-4 h-4 text-amber flex-shrink-0 mt-1" />
                <span>{v}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Section 5 · Governance */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-b border-white/10">
        <SectionKicker>Section 5 · Governance</SectionKicker>
        <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-3xl">
          Light. Intentional.
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-white/10 bg-ink-800 p-6">
            <div className="overline text-amber mb-2">Founding partners</div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              <span className="text-white">Sanjeev Bhandari</span> (TMG Tech
              Ventures) and <span className="text-white">Vijay Gummadi</span>{' '}
              (Autorox) handle network-level decisions only — admitting new
              members and amending this charter. Everything else remains
              voluntary and member-driven.
            </p>
          </div>
          <div className="border border-white/10 bg-ink-800 p-6">
            <div className="overline text-amber mb-2">Removal mechanism</div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              A clean exit for rare cases. Any member may be removed by a{' '}
              <span className="text-amber">unanimous vote</span> of the
              remaining members with{' '}
              <span className="text-amber">seven days&apos; notice</span>.
              Protects the group&apos;s quality without creating an appeals
              bureaucracy.
            </p>
          </div>
        </div>
      </section>

      {/* Section 6 · Path forward */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-b border-white/10 bg-ink-900/30">
        <SectionKicker>Section 6 · Path forward</SectionKicker>
        <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-3xl">
          Start light. Add structure only when it earns its place.
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-white/10 bg-ink-800 p-6">
            <div className="overline text-amber mb-2">Overhead — zero, by design</div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              Permanent staff, budgets, committees and formal overhead are
              work for everyone. If the network clearly generates enough
              value to justify more structure, we build it then — when the
              need is proven.
            </p>
          </div>
          <div className="border border-white/10 bg-ink-800 p-6">
            <div className="overline text-amber mb-2">Shared ownership — later, if at all</div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              We start with zero shared ownership. We want to see whether
              collaboration actually generates real business first. After a
              year or so, if members feel deeper alignment would be useful,
              we can have that conversation grounded in actual results.
            </p>
          </div>
        </div>

        <div className="mt-10 border border-amber/40 bg-amber/5 p-6 md:p-8">
          <div className="overline text-amber mb-3">First milestone</div>
          <ul className="space-y-2 text-sm md:text-base text-zinc-200 leading-relaxed">
            <li className="flex gap-3"><span className="text-amber">01</span> Bring 5–6 solid companies into the network.</li>
            <li className="flex gap-3"><span className="text-amber">02</span> Make at least one joint OEM or dealer pitch.</li>
            <li className="flex gap-3"><span className="text-amber">03</span> Convert that into actual revenue.</li>
          </ul>
          <p className="mt-4 text-xs text-zinc-400 leading-relaxed">
            If that works, momentum builds naturally.
          </p>
        </div>
      </section>

      {/* Section 7 · Operating rhythm */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-b border-white/10">
        <SectionKicker>Section 7 · Operating rhythm</SectionKicker>
        <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-3xl">
          Regular. Short. Focused.
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {RHYTHM.map((r) => (
            <div key={r.cadence} data-testid={`rhythm-${r.cadence.toLowerCase()}`} className="border border-white/10 bg-ink-800 p-6 flex flex-col">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber">{r.cadence}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mt-1">{r.duration}</div>
              <h3 className="mt-4 font-display text-lg text-white leading-tight">{r.title}</h3>
              <p className="mt-3 text-xs text-zinc-400 leading-relaxed">{r.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Section 8 · What we track */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <SectionKicker>Section 8 · What we track</SectionKicker>
        <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-3xl">
          Outcomes. If the numbers go up, the model works.
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {METRICS.map((m, i) => (
            <div key={i} className="border border-white/10 bg-ink-800 p-8 text-center">
              <div className="font-display text-6xl text-amber font-bold">{String(i + 1).padStart(2, '0')}</div>
              <div className="mt-4 text-sm text-zinc-200 leading-relaxed">{m}</div>
            </div>
          ))}
        </div>

        <div className="mt-16 border-t border-white/10 pt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-4">For TMG Tech Ventures Pvt. Ltd.</div>
            <div className="h-16 border-b border-white/15" />
            <div className="mt-3 text-sm text-zinc-400">Sanjeev Bhandari · Founding partner</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-4">For Autorox</div>
            <div className="h-16 border-b border-white/15" />
            <div className="mt-3 text-sm text-zinc-400">Vijay Gummadi · Founding partner</div>
          </div>
        </div>

        <p className="mt-10 max-w-3xl text-xs text-zinc-500 leading-relaxed">
          This charter is a working document among the founding partners and
          admitted members. It is non-binding and may be amended by the
          founding partners. Any binding commercial arrangements between
          members are governed by their bilateral agreements. Distribution
          restricted to named parties.
        </p>
      </section>
    </PageShell>
  );
};

export default DraftStructure;
